import React, { useState, useEffect } from "react";
import {
  Center,
  FormLabel,
  HStack,
  Box,
  Button,
  Image,
  VStack,
  Text,
  Heading,
  Spacer,
  Stack,
  SimpleGrid,
  GridItem,
  Divider,
  useToast,
  Checkbox,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Navbar from "../components/navbar/Navbar";
import AvatarMenu from "../components/navbar/avatar-menu";
import HomeSidebarContent from "../components/home/home-sidebar-content";
import NavbarLinks from "../components/navbar/NavbarLinks";
import LoadingSpinner from "../components/ui/loading-spinner";
import ResponsiveDateRangePicker from "../components/date/date-picker";
import Footer from "../components/footer";
const backendUrl = process.env.REACT_APP_BACKEND_URL;

function Rent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [car, setCar] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [includeDriver, setIncludeDriver] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);

  useEffect(() => {
    axios
      .get(`${backendUrl}/api/cars/${id}`)
      .then((response) => {
        setCar(response.data.data.car);
        setBookedDates(response.data.data.availability);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching car data:", error);
        setLoading(false);
      });
  }, [id]);

  const validateDates = (startDate, endDate) => {
    let validationErrors = [];
    const now = new Date().getTime();
    const rentDuration = endDate - startDate;

    if (endDate < now) {
      validationErrors.push(
        "Return date cannot be in the past. Please select a valid return date.",
      );
    }

    if (validationErrors.length > 0) {
      toast({
        title: "Info",
        description: validationErrors.join(" "),
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }

    return validationErrors.length === 0;
  };

  const calculatePrice = (startDate, endDate, includeDriver, car) => {
    const rentDuration = endDate - startDate;
    if (rentDuration > 0) {
      const days = Math.round(rentDuration / (1000 * 60 * 60 * 24));
      console.log('days', days)
      const driverFee = includeDriver ? days * 15 : 0;
      console.log('driver fee', driverFee)
      const price = days * car.price + driverFee;
      console.log('total price', price)
      setTotalPrice(price);
    } else {
      setTotalPrice(0);
    }
  };

  const handleDateChange = (dates) => {
    const [startDate, endDate] = dates;
    setDateRange(dates);

    if (endDate) {
      if (validateDates(startDate, endDate)) {
        calculatePrice(startDate, endDate, includeDriver, car);
      } else {
        setTotalPrice(0);
      }
    }
  };

  const handleIncludeDriverChange = (e) => {
    const newIncludeDriver = e.target.checked;
    setIncludeDriver(newIncludeDriver);

    if (dateRange[1] && validateDates(dateRange[0], dateRange[1])) {
      calculatePrice(dateRange[0], dateRange[1], newIncludeDriver, car);
    }
  };

  const rentACar = async (e) => {
    e.preventDefault();
    if (!validateDates(dateRange[0], dateRange[1])) {
      return;
    }

    console.log("car.name", car);

    navigate("/checkout", {
      state: {
        rental_date: dateRange[0].toISOString(),
        return_date: dateRange[1].toISOString(),
        price: totalPrice,
        car_id: id,
        include_driver: includeDriver,
        photo: car.photo2,
        car_name: car.brand,
      },
    });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <Navbar
        sidebarContent={<HomeSidebarContent />}
        links={<NavbarLinks />}
        buttons={<AvatarMenu />}
      />
      <Center>
        <Stack
          direction={{ base: "column", lg: "row" }}
          boxShadow="2xl"
          h={"auto"}
          w={"100%"}
          borderRadius="15px"
          overflow={"hidden"}
          margin={"1rem 3% 3rem 3%"}
        >
          <Box w={{ base: "100%", lg: "50%" }}>
            <Image src={car.photo2} objectFit="cover" h={"full"} />
          </Box>
          <Box w={{ base: "100%", lg: "50%" }} p={"5%"} bg={"white"} h={"full"}>
            <VStack alignItems={"center"} spacing={"3"}>
              <Heading fontWeight={"500"}>{car.brand}</Heading>

              <FormLabel fontWeight="600" color="gray.600">
                {t("profile.rentalPeriod")}
              </FormLabel>
              <ResponsiveDateRangePicker
                blockedDates={bookedDates}
                value={dateRange}
                onChange={handleDateChange}
              />

              <Divider borderColor="gray.300" py={3} />
              <SimpleGrid w={"full"} columns={3} py={3} textAlign="center">
                <GridItem>
                  <Heading fontWeight="500" color="gray.400" size="xs">
                    {t("profile.gearbox")}
                  </Heading>
                  <Text fontWeight="600" color="gray.600">
                    {car.gearbox === "automatic" || car.gearbox === "manuel"
                      ? t(`carCard.${car.gearbox.toLowerCase()}`)
                      : car.fuel_type}
                  </Text>
                </GridItem>
                <GridItem>
                  <Heading fontWeight="500" color="gray.400" size="xs">
                    {t("profile.type")}
                  </Heading>
                  <Text fontWeight="600" color="gray.600">
                    {car.fuel_type === "petrol" || car.fuel_type === "diesel"
                      ? t(`carCard.${car.fuel_type.toLowerCase()}`)
                      : car.fuel_type}
                  </Text>
                </GridItem>
                <GridItem>
                  <Heading fontWeight="500" color="gray.400" size="xs">
                    {t("carCard.price")}
                  </Heading>
                  <Text fontWeight="600" color="gray.600">
                    {car.price}&nbsp;{t('carCard.perDay')}
                  </Text>
                </GridItem>
              </SimpleGrid>
              <Divider borderColor="gray.300" py={0} />
              <Checkbox
                isChecked={includeDriver}
                onChange={handleIncludeDriverChange}
              >
                Include driver
              </Checkbox>
              <Divider borderColor="gray.300" py={0} />
              <HStack w={"full"} justify={"space-between"}>
                <Text fontWeight="600" color="gray.600">
                  Total
                </Text>
                <Spacer />
                <Text
                  color="gray.600"
                  fontSize="2xl"
                  fontWeight={["bold", "extrabold"]}
                >
                  ${totalPrice.toFixed(2)}
                </Text>
                <Text ml={2} fontSize="xl" fontWeight="medium" color="gray.500">
                  USD
                </Text>
              </HStack>
              <Button onClick={rentACar} w={"full"}>
                {t("carCard.goToCheckout")}
              </Button>
            </VStack>
          </Box>
        </Stack>
      </Center>
      <Footer />
    </>
  );
}

export default Rent;
